import {
  TextMessagePart,
  ThreadAssistantMessagePart,
  ThreadUserMessagePart,
} from "@assistant-ui/react";

import type { SelectDateRangeState } from "../../../../components/SelectDateRange/SelectDateRange";
import {
  endDateInSeconds,
  formatLocalISODate,
  startDateInSeconds,
} from "../../../../utils/datetime";
import type {
  FilterState,
  InterviewTypeFilter,
  MaxInterviewCountOption,
} from "./CustomInsightsFiltersProvider";

/* eslint-disable camelcase -- tool names match backend */
export const TOOL_LABELS: Record<string, string> = {
  search: "Searching interviews",
  get_call_ai_notes: "Fetching AI notes",
  get_transcripts: "Fetching full transcripts",
  get_job_description: "Loading job description",
  get_interview_guide: "Loading interview guides",
  render_chart: "Rendering chart",
  manage_todos: "Planning",
  get_skill: "Loading instructions",
  code_execution: "Running code",
  web_search: "Searching the web",
};
/* eslint-enable camelcase */

export type InsightsChartData = {
  toolCallId: string;
  messageId: string;
  title: string;
  labels: string[];
  chart_type?: string; // eslint-disable-line camelcase
  values?: number[];
  series?: Array<{ name: string; values: number[] }>;
};

export type RerunReport = {
  id: string;
  title: string;
  filters: string;
  prompt: string;
  summary?: string;
  charts?: InsightsChartData[];
};

export type FilterHistoryEntry = {
  timestamp: number;
  state: FilterState;
};

export type ThreadMessagePart =
  | { type: "text"; text?: string }
  | { type: "tool-call"; toolCallId: string; toolName: string; args: unknown };

export type ThreadMessage = {
  id: string;
  role: string;
  content?: ThreadMessagePart[];
  parts?: ThreadMessagePart[];
};

export type ChartArgs = {
  title: string;
  labels: string[];
  chart_type?: string;
  values?: number[];
  series?: Array<{ name: string; values: number[] }>;
};

export function buildFiltersMessageForAssistant({
  callNameSearch,
  dateRange,
  selectedPositions,
  selectedInterviewers,
  selectedDepartments,
  selectedStages,
  selectedLocations,
  selectedCandidateOutcomes,
  durationRange,
  interviewType,
  maxInterviewCount,
}: {
  callNameSearch: string;
  dateRange: { start: Date; end: Date };
  selectedPositions: readonly { id: string; displayTitle: string }[];
  selectedInterviewers: { label?: string | null; value: string }[];
  selectedDepartments: { label?: string | null; value: string }[];
  selectedStages: { label?: string | null; value: string }[];
  selectedLocations: { label?: string | null; value: string }[];
  selectedCandidateOutcomes: { label?: string | null; value: string }[];
  durationRange: { minSeconds: number; maxSeconds: number };
  interviewType: InterviewTypeFilter;
  maxInterviewCount: MaxInterviewCountOption;
}): string {
  const startDate = dateRange?.start instanceof Date ? dateRange.start : null;
  const endDate = dateRange?.end instanceof Date ? dateRange.end : null;
  const parts: string[] = ["SET_FILTERS:"];
  if (startDate && endDate) {
    const startStr = formatLocalISODate(startDate);
    const endStr = formatLocalISODate(endDate);
    const minDateUnix = startDateInSeconds(startDate);
    const maxDateUnix = endDateInSeconds(endDate);
    parts.push(
      `Date range: ${startStr} to ${endStr} (min_date=${minDateUnix}, max_date=${maxDateUnix})`
    );
  }
  if (callNameSearch) {
    parts.push(`Search term (title): ${callNameSearch}`);
  }
  if (durationRange.minSeconds !== 120 || durationRange.maxSeconds !== 7200) {
    parts.push(
      `Duration: ${durationRange.minSeconds}s to ${durationRange.maxSeconds}s`
    );
  }
  if (interviewType != null) {
    parts.push(
      `Interview type: ${interviewType === "screen" ? "Screen" : "Core"}`
    );
  }
  parts.push(`Max interview count: ${maxInterviewCount}`);
  if (selectedPositions.length > 0) {
    const ids = selectedPositions.map((p) => p.id).filter(Boolean);
    const titles = selectedPositions
      .map((p) => (p?.displayTitle != null ? String(p.displayTitle) : ""))
      .filter(Boolean);
    parts.push(`Position IDs: ${ids.join(", ")} (${titles.join(", ")})`);
  }
  if (selectedInterviewers.length > 0) {
    const interviewerEmails = selectedInterviewers
      .map((i) => i?.value ?? "")
      .filter(Boolean);
    const interviewerNames = selectedInterviewers
      .map((i) => i?.label ?? "")
      .filter(Boolean);
    parts.push(
      `Interviewer Emails: ${interviewerEmails.join(
        ", "
      )} (${interviewerNames.join(", ")})`
    );
  }
  if (selectedDepartments.length > 0) {
    parts.push(
      `Departments: ${selectedDepartments
        .map((d) => d?.value ?? d?.label ?? "")
        .filter(Boolean)
        .join(", ")}`
    );
  }
  if (selectedStages.length > 0) {
    parts.push(
      `Stages: ${selectedStages
        .map((s) => s?.value ?? s?.label ?? "")
        .filter(Boolean)
        .join(", ")}`
    );
  }
  if (selectedLocations.length > 0) {
    parts.push(
      `Locations: ${selectedLocations
        .map((l) => l?.value ?? l?.label ?? "")
        .filter(Boolean)
        .join(", ")}`
    );
  }
  if (selectedCandidateOutcomes.length > 0) {
    parts.push(
      `Candidate Outcomes: ${selectedCandidateOutcomes
        .map((c) => c?.label ?? c?.value ?? "")
        .filter(Boolean)
        .join(", ")}`
    );
  }
  const [prefix, ...filters] = parts;
  return filters.length > 0 ? `${prefix} ${filters.join(". ")}` : prefix;
}

/**
 * Accepts both in-memory FilterState (Date objects) and JSON-deserialized
 * filter snapshots (date strings).
 */
export type SerializableFilterState = Partial<
  Omit<FilterState, "dateRange"> & {
    dateRange: {
      start: Date | string;
      end: Date | string;
      type?: SelectDateRangeState["type"];
    };
  }
>;

export function serializeFilterState(
  state: FilterState
): SerializableFilterState {
  return {
    callNameSearch: state.callNameSearch,
    dateRange: {
      start: state.dateRange.start.toISOString(),
      end: state.dateRange.end.toISOString(),
      type: state.dateRange.type,
    },
    selectedPositions: state.selectedPositions,
    selectedInterviewers: state.selectedInterviewers,
    selectedDepartments: state.selectedDepartments,
    selectedStages: state.selectedStages,
    selectedLocations: state.selectedLocations,
    selectedCandidateOutcomes: state.selectedCandidateOutcomes,
    durationRange: state.durationRange,
    interviewType: state.interviewType,
    maxInterviewCount: state.maxInterviewCount,
  };
}

export function getFilterSnapshot(
  filterHistory: readonly FilterHistoryEntry[],
  messageIdToFilterTimestamp: Readonly<Record<string, number>>,
  messageId: string
): FilterState | null {
  const ts = messageIdToFilterTimestamp[messageId];
  if (ts == null) return null;
  const entry = filterHistory.find((e) => e.timestamp === ts);
  return entry?.state ?? null;
}

export function getMessageSummary(
  message: ThreadMessage | null | undefined
): string {
  if (!message) return "";
  const content = message.content ?? message.parts ?? [];
  return content
    .filter((p) => p.type === "text")
    .map((p) => {
      if ("text" in p) {
        return stripSkillPrefix(p.text ?? "");
      }
      return "";
    })
    .join("\n\n");
}

export function parseChartArgs(args: ChartArgs | unknown): ChartArgs | null {
  if (!args) return null;
  if (typeof args === "string") {
    try {
      return JSON.parse(args) as ChartArgs;
    } catch {
      return null;
    }
  }
  return args as ChartArgs;
}

export function deriveChartsFromMessage(
  message: ThreadMessage | null | undefined,
  messageId: string
): InsightsChartData[] {
  if (!message) return [];
  const parts = message.content ?? message.parts ?? [];
  return parts.flatMap((p): InsightsChartData[] => {
    if (p.type !== "tool-call" || !("toolName" in p)) return [];
    if (p.toolName !== "render_chart") return [];
    const args = parseChartArgs(p.args);
    if (!args) return [];
    const hasData =
      (args.values?.length ?? 0) > 0 || (args.series?.length ?? 0) > 0;
    if (!args.labels?.length || !hasData) return [];
    return [
      {
        toolCallId: p.toolCallId,
        messageId,
        title: args.title,
        labels: args.labels,
        chart_type: args.chart_type, // eslint-disable-line camelcase
        values: args.values,
        series: args.series,
      },
    ];
  });
}

export type SaveReportVariables = {
  title: string;
  messages: string;
  result: string;
  filters: string;
};

export function isSetFiltersMessage(
  content:
    | readonly ThreadMessagePart[]
    | readonly ThreadUserMessagePart[]
    | readonly [TextMessagePart]
    | readonly ThreadAssistantMessagePart[]
): boolean {
  const firstTextPart = content?.find((part) => part.type === "text");
  return Boolean(
    firstTextPart &&
      "text" in firstTextPart &&
      firstTextPart.text?.startsWith("SET_FILTERS:")
  );
}

export function stripSkillPrefix(text: string): string {
  return text.replace(/^\[SKILL:\w+\]\s*/, "");
}

export function shouldSummarizeMessage(message: ThreadMessage): boolean {
  return (
    (message.role === "user" || message.role === "assistant") &&
    !isSetFiltersMessage(message.content ?? [])
  );
}

export function buildSaveReportVariables({
  messageId,
  title,
  messages,
  filterHistory,
  messageIdToFilterTimestamp,
}: {
  messageId: string;
  title: string;
  messages: ThreadMessage[];
  filterHistory: readonly FilterHistoryEntry[];
  messageIdToFilterTimestamp: Readonly<Record<string, number>>;
}): SaveReportVariables {
  const assistantMsg = messages.find((m) => m.id === messageId);
  const charts = deriveChartsFromMessage(assistantMsg, messageId);
  const summary = getMessageSummary(assistantMsg);
  const resultJson = JSON.stringify({ summary, charts });

  const textMessages = messages
    .filter(shouldSummarizeMessage)
    .map((m) => ({ role: m.role, content: getMessageSummary(m) }))
    .filter((m) => m.content.trim());

  const snapshot = getFilterSnapshot(
    filterHistory,
    messageIdToFilterTimestamp,
    messageId
  );
  const serializedFilters = snapshot ? serializeFilterState(snapshot) : null;

  return {
    title: title.trim(),
    messages: JSON.stringify(textMessages),
    result: resultJson,
    filters: JSON.stringify(serializedFilters),
  };
}
